import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { BoxStaffCoOwnerIcon } from "@images/svgIcons/boxStaff/boxStaffCoOwnerIcon"
import { BoxStaffCoachIcon } from "@images/svgIcons/boxStaff/boxStaffCoachIcon"
import { BoxStaffHeadCoachIcon } from "@images/svgIcons/boxStaff/boxStaffHeadCoachIcon"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { UnionIcon } from "@images/svgIcons/unionIcon"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffSidebar } from "@modules/boxStaff/hooks/useBoxStaffSidebar"
import { word } from "@modules/core/utils/i18n"
import { Check, X } from "lucide-react"
import { type ReactNode } from "react"

type BoxStaffCardsViewProps = {
  className?: string
} & React.ComponentProps<"div">

export const BoxStaffCardsView = ({ className, ...props }: BoxStaffCardsViewProps) => {
  const { navigateToAddBoxStaffForm } = useBoxStaffSidebar()

  return (
    <div className={cn("flex flex-col gap-4 px-5 pt-12 pb-8", className)} {...props}>
      <Text fontStyle="TL">{word("box.member.cardsView.title")}</Text>
      <div className="mt-8 grid grid-cols-2 gap-4">
        {getStaffRoles().map((staff) => (
          <BoxStaffCard key={staff.role} {...staff} onClick={() => navigateToAddBoxStaffForm(staff.role)} />
        ))}
      </div>
    </div>
  )
}

type BoxStaffCardConstructor = {
  permissions: { text: string; check: boolean }[]
  onClick: () => void
  icon: ReactNode
  role: BoxStaffRole
  className?: string
} & React.ComponentProps<"div">

const BoxStaffCard = ({ icon, permissions, onClick, role, className, ...props }: BoxStaffCardConstructor) => {
  return (
    <div
      className={cn(
        "relative isolate flex h-full flex-col gap-4 overflow-hidden rounded-[20px] bg-interactive-background-neutral-disabled p-8",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2">
        {icon}
        <Text fontStyle="TM" className="font-bold text-textDefault-body">
          {displayRole(role)}
        </Text>
      </div>
      <div className="flex flex-col gap-4">
        <Text fontStyle="LS" className="self-start text-textOnSurface-subtle">
          {word("box.member.cardsView.permissions")}
        </Text>
        {permissions.map((permission, index) => (
          <div key={`${index}-${permission.text}`} className="flex w-full items-center gap-2">
            {permission.check ? (
              <Check size={24} color="currentColor" className="shrink-0 text-textSuccess" />
            ) : (
              <X size={24} color="currentColor" className="shrink-0 text-textError" />
            )}
            <Text fontStyle="LM" className="text-textDefault-body">
              {permission.text}
            </Text>
          </div>
        ))}
      </div>
      <Button variant="primary" onClick={onClick} className="mt-auto w-full">
        <PlusIcon size={14} color="currentColor" />
        {`${word("add.box.member.cardsView.add")} ${displayRole(role).toLowerCase()}`}
      </Button>

      <UnionIcon
        color="currentColor"
        className="absolute -right-2.5 -bottom-2.5 -z-1 size-45 text-textDefault-body opacity-25"
      />
    </div>
  )
}

const getStaffRoles: () => {
  role: Exclude<BoxStaffRole, "READ_ONLY">
  permissions: { text: string; check: boolean }[]
  description: string
  icon: React.ReactNode
}[] = () => [
  {
    role: BoxStaffRole.COACH,
    permissions: [
      {
        text: word("huPermissions.features.staffCards.coach.feature1"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.coach.feature2"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.coach.feature3"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.coach.feature4"),
        check: true,
      },
    ],
    description: word("box.staff.permissions.description.COACH"),
    icon: <BoxStaffCoachIcon color={""} size={50} />,
  },
  {
    role: BoxStaffRole.HEAD_COACH,
    permissions: [
      {
        text: word("huPermissions.features.staffCards.headCoach.feature1"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.headCoach.feature2"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.headCoach.feature3"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.headCoach.feature4"),
        check: true,
      },
    ],
    description: word("box.staff.permissions.description.HEAD_COACH"),
    icon: <BoxStaffHeadCoachIcon color={""} size={50} />,
  },
  {
    role: BoxStaffRole.CO_OWNER,
    permissions: [
      {
        text: word("huPermissions.features.staffCards.coOwner.feature1"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.coOwner.feature2"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.coOwner.feature3"),
        check: true,
      },
      {
        text: word("huPermissions.features.staffCards.coOwner.feature4"),
        check: false,
      },
    ],
    description: word("box.staff.permissions.description.CO_OWNER"),
    icon: <BoxStaffCoOwnerIcon color={""} size={50} />,
  },
]
